import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Badge } from './ui/badge.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs.jsx';
import { 
  Search, 
  Filter, 
  Plus, 
  Edit, 
  Trash2, 
  Package, 
  DollarSign,
  Building,
  Wrench,
  Settings,
  X
} from 'lucide-react';

const Consumables = () => {
  const [consumables, setConsumables] = useState([]);
  const [filteredConsumables, setFilteredConsumables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showManageCategories, setShowManageCategories] = useState(false);
  const [showManageSuppliers, setShowManageSuppliers] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // Default to list view
  const [suggestions, setSuggestions] = useState([]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Auth user comes from context (localStorage only holds the token)
  const { user } = useAuth();
  const token = localStorage.getItem('token');

  // Role-based capabilities (mirrors backend CONSUMABLE_* role sets)
  const canEditItems = ['super_admin', 'admin', 'dispatch', 'fabrication_manager', 'project_manager', 'project_manager_estimator'].includes(user?.role);
  const canManageCategories = ['super_admin', 'admin', 'dispatch', 'project_manager', 'project_manager_estimator'].includes(user?.role);
  const canManage = canEditItems;

  useEffect(() => {
    fetchConsumables();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (canEditItems) fetchSuggestions();
  }, [user]);

  const fetchSuggestions = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/consumables/suggestions?status=pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    }
  };

  const actOnSuggestion = async (sid, action) => {
    try {
      const response = await fetch(`${backendUrl}/api/consumables/suggestions/${sid}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (response.ok) {
        toast.success(action === 'approve'
          ? 'Approved — it\'s now in the timesheet consumables list for everyone'
          : 'Suggestion rejected');
        setSuggestions(prev => prev.filter(s => s.id !== sid));
      } else {
        toast.error('Action failed');
      }
    } catch (err) {
      toast.error('Action failed');
    }
  };

  useEffect(() => {
    filterConsumables();
  }, [consumables, searchTerm, selectedCategory, selectedSupplier]);

  const fetchConsumables = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/consumables`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConsumables(data);
      } else {
        setError('Failed to fetch consumables');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error fetching consumables:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/consumables/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
        setSuppliers(data.suppliers || []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const filterConsumables = () => {
    let filtered = consumables;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item => 
        (item.item_name || item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.category || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.brand || item.supplier || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.size || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // Filter by supplier/brand
    if (selectedSupplier !== 'all') {
      filtered = filtered.filter(item => 
        (item.supplier === selectedSupplier) || (item.brand === selectedSupplier)
      );
    }

    setFilteredConsumables(filtered);
  };

  const handleAddItem = async (itemData) => {
    try {
      const response = await fetch(`${backendUrl}/api/consumables`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(itemData)
      });

      if (response.ok) {
        await fetchConsumables();
        setShowAddForm(false);
      } else {
        setError('Failed to add consumable');
      }
    } catch (err) {
      setError('Error adding consumable');
      console.error('Error adding consumable:', err);
    }
  };

  const handleEditItem = async (id, itemData) => {
    try {
      const response = await fetch(`${backendUrl}/api/consumables/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(itemData)
      });

      if (response.ok) {
        await fetchConsumables();
        setEditingItem(null);
      } else {
        setError('Failed to update consumable');
      }
    } catch (err) {
      setError('Error updating consumable');
      console.error('Error updating consumable:', err);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this consumable?')) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/consumables/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchConsumables();
      } else {
        setError('Failed to delete consumable');
      }
    } catch (err) {
      setError('Error deleting consumable');
      console.error('Error deleting consumable:', err);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const getUniqueValues = (items, field) => {
    const values = items.map(item => item[field]).filter(Boolean);
    return [...new Set(values)].sort();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading consumables...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Consumables & Materials</h1>
            <p className="text-slate-600 mt-1">
              Manage inventory and pricing for quotes ({filteredConsumables.length} items)
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            {canManageCategories && (
              <button
                data-testid="manage-categories-btn"
                onClick={() => setShowManageCategories(true)}
                className="bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Settings className="h-4 w-4" />
                Categories
              </button>
            )}
            <button
              data-testid="manage-suppliers-btn"
              onClick={() => setShowManageSuppliers(true)}
              className="bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Building className="h-4 w-4" />
              Suppliers
            </button>
            <button
              data-testid="add-consumable-btn"
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Item
            </button>
          </div>
        )}
      </div>

      {/* Pending field suggestions */}
      {canEditItems && suggestions.length > 0 && (
        <Card className="bg-amber-50 border-2 border-amber-300 mb-6" data-testid="consumable-suggestions-card">
          <CardContent className="p-4">
            <h2 className="text-sm font-bold text-amber-900 uppercase tracking-wide mb-1">
              New consumables from the field — pending approval
            </h2>
            <p className="text-xs text-amber-700 mb-3">
              Typed in on daily timesheets. Approving adds the item to the consumables list for everyone.
            </p>
            <div className="space-y-2">
              {suggestions.map((s) => (
                <div key={s.id} className="flex items-center justify-between bg-white rounded-lg border border-amber-200 px-3 py-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 text-sm truncate">{s.name}</p>
                    <p className="text-xs text-slate-500">
                      Suggested by {s.suggested_by} · {new Date(s.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                    <button
                      onClick={() => actOnSuggestion(s.id, 'approve')}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold"
                      data-testid={`suggestion-approve-${s.id}`}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => actOnSuggestion(s.id, 'reject')}
                      className="px-3 py-1.5 bg-white border border-red-300 text-red-600 hover:bg-red-50 rounded-lg text-xs font-semibold"
                      data-testid={`suggestion-reject-${s.id}`}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="bg-white/60 backdrop-blur-xl border-slate-200 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search consumables..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="min-w-48">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category.id || category.name} value={category.name}>{category.name}</option>
                ))}
              </select>
            </div>

            {/* Supplier Filter */}
            <div className="min-w-48">
              <select
                value={selectedSupplier}
                onChange={(e) => setSelectedSupplier(e.target.value)}
                className="w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Suppliers</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id || supplier.name} value={supplier.name}>{supplier.name}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* View Mode Toggle */}
      <div className="flex justify-end mb-4">
        <div className="bg-white rounded-lg p-1 shadow-sm">
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            List View
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`px-4 py-2 rounded ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            Grid View
          </button>
        </div>
      </div>

      {/* Categories Tabs */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList className="bg-slate-900/10 backdrop-blur-sm">
          <TabsTrigger value="all" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:text-slate-800 data-[state=inactive]:bg-white/60">
            All Items ({filteredConsumables.length})
          </TabsTrigger>
          {getUniqueValues(consumables, 'category').slice(0, 4).map(category => {
            const count = filteredConsumables.filter(item => item.category === category).length;
            return (
              <TabsTrigger key={category} value={category} className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:text-slate-800 data-[state=inactive]:bg-white/60">
                {category} ({count})
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="all">
          <ConsumablesList 
            items={filteredConsumables}
            canManage={canManage}
            onEdit={setEditingItem}
            onDelete={handleDeleteItem}
            formatPrice={formatPrice}
            viewMode={viewMode}
          />
        </TabsContent>

        {getUniqueValues(consumables, 'category').slice(0, 4).map(category => (
          <TabsContent key={category} value={category}>
            <ConsumablesList 
              items={filteredConsumables.filter(item => item.category === category)}
              canManage={canManage}
              onEdit={setEditingItem}
              onDelete={handleDeleteItem}
              formatPrice={formatPrice}
              viewMode={viewMode}
            />
          </TabsContent>
        ))}
      </Tabs>

      {/* Add/Edit Forms */}
      {showAddForm && (
        <ConsumableForm
          onSubmit={handleAddItem}
          onClose={() => setShowAddForm(false)}
          categories={categories}
          suppliers={suppliers}
          canManageCategories={canManageCategories}
          canEditItems={canEditItems}
          token={token}
          backendUrl={backendUrl}
          onListsChanged={fetchCategories}
          title="Add New Consumable"
        />
      )}

      {editingItem && (
        <ConsumableForm
          item={editingItem}
          onSubmit={(data) => handleEditItem(editingItem.id, data)}
          onClose={() => setEditingItem(null)}
          categories={categories}
          suppliers={suppliers}
          canManageCategories={canManageCategories}
          canEditItems={canEditItems}
          token={token}
          backendUrl={backendUrl}
          onListsChanged={fetchCategories}
          title="Edit Consumable"
        />
      )}

      {showManageCategories && (
        <ManageCategoriesModal
          categories={categories}
          token={token}
          backendUrl={backendUrl}
          onClose={() => setShowManageCategories(false)}
          onChanged={() => { fetchCategories(); fetchConsumables(); }}
        />
      )}

      {showManageSuppliers && (
        <ManageSuppliersModal
          suppliers={suppliers}
          token={token}
          backendUrl={backendUrl}
          onClose={() => setShowManageSuppliers(false)}
          onChanged={() => { fetchCategories(); fetchConsumables(); }}
        />
      )}
      </div>
    </div>
  );
};

// Consumables List Component
const ConsumablesList = ({ items, canManage, onEdit, onDelete, formatPrice, viewMode }) => {
  if (items.length === 0) {
    return (
      <Card className="bg-white/60 backdrop-blur-xl border-slate-200 hover:bg-white/70 transition-all">
        <CardContent className="p-8 text-center">
          <Package className="h-12 w-12 text-slate-500 mx-auto mb-4" />
          <p className="text-slate-500">No consumables found matching your criteria.</p>
        </CardContent>
      </Card>
    );
  }

  // List View
  if (viewMode === 'list') {
    return (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Markup</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sell Price</th>
              {canManage && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{item.item_name || item.name}</div>
                  {item.description && (
                    <div className="text-sm text-gray-500">{item.description}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                    {item.category}
                  </Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.size || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.brand || item.supplier || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                  {formatPrice(item.unit_price || 0)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {(item.markup_percentage || 0)}%{item.markup_overridden && <span className="ml-1 text-[10px] text-amber-600 font-semibold" title="Manually overridden">(pinned)</span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-emerald-700">
                  {formatPrice(item.final_price != null ? item.final_price : (item.unit_price || 0) * (1 + (item.markup_percentage || 0) / 100))}
                </td>
                {canManage && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      <button
                        onClick={() => onEdit(item)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onDelete(item.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Grid View
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((item) => (
        <Card key={item.id} className="bg-white border-slate-200 hover:border-slate-300 transition-colors">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className="text-slate-900 text-lg leading-tight">
                  {item.item_name || item.name}
                </CardTitle>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-600">
                    {item.category}
                  </Badge>
                  {(item.brand || item.supplier) && (
                    <Badge variant="outline" className="border-slate-300 text-slate-700">
                      {item.brand || item.supplier}
                    </Badge>
                  )}
                </div>
              </div>
              {canManage && (
                <div className="flex gap-1 ml-2">
                  <button
                    onClick={() => onEdit(item)}
                    className="p-1 text-slate-500 hover:text-blue-700 transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(item.id)}
                    className="p-1 text-slate-500 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {/* Price Information */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-500">Unit Price:</span>
                </div>
                <span className="text-slate-900 font-semibold">
                  {formatPrice(item.unit_price || 0)}
                </span>
              </div>

              {/* Box/Bulk Price */}
              {item.box_price && item.box_quantity && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-slate-500" />
                    <span className="text-slate-500">Box ({item.box_quantity}):</span>
                  </div>
                  <span className="text-slate-900 font-semibold">
                    {formatPrice(item.box_price)}
                  </span>
                </div>
              )}

              {/* Size/Specifications */}
              {item.size && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-slate-500" />
                    <span className="text-slate-500">Size:</span>
                  </div>
                  <span className="text-slate-700">{item.size}</span>
                </div>
              )}

              {/* Unit */}
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Unit:</span>
                <span className="text-slate-700">{item.unit || 'Each'}</span>
              </div>

              {/* Description */}
              {item.description && (
                <div className="mt-3 p-2 bg-slate-100 rounded text-sm text-slate-700">
                  {item.description}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Consumable Form Component (Add/Edit)
const ConsumableForm = ({ item = null, onSubmit, onClose, categories = [], suppliers = [], canManageCategories = false, canEditItems = false, token, backendUrl, onListsChanged, title }) => {
  const [formData, setFormData] = useState({
    name: item?.name || item?.item_name || '',
    category: item?.category || '',
    supplier: item?.supplier || item?.brand || '',
    unit_price: item?.unit_price ?? '',
    box_price: item?.box_price ?? '',
    box_quantity: item?.box_quantity ?? '',
    size: item?.size || '',
    unit: item?.unit || 'Each',
    description: item?.notes || item?.description || '',
    markup_percentage: item?.markup_percentage != null ? item.markup_percentage : '',
  });
  const [markupTouched, setMarkupTouched] = useState(false);

  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatMarkup, setNewCatMarkup] = useState('');
  const [isCustomSupplier, setIsCustomSupplier] = useState(false);
  const [newSup, setNewSup] = useState({ name: '', contact_number: '', account_manager: '', email: '' });
  const [savingAux, setSavingAux] = useState(false);

  const categoryMarkup = (name) => {
    const c = categories.find(x => x.name === name);
    return c ? Number(c.markup_percentage || 0) : 0;
  };

  const selectCategory = (name) => {
    setFormData(prev => ({
      ...prev,
      category: name,
      markup_percentage: markupTouched ? prev.markup_percentage : categoryMarkup(name),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.category) { alert('Please select a category'); return; }
    const submitData = {
      name: formData.name,
      category: formData.category,
      supplier: formData.supplier || '',
      size: formData.size || null,
      unit: formData.unit || 'Each',
      notes: formData.description || null,
      unit_price: parseFloat(formData.unit_price) || 0,
      box_price: formData.box_price ? parseFloat(formData.box_price) : null,
      box_quantity: formData.box_quantity ? parseInt(formData.box_quantity) : null,
      markup_percentage: formData.markup_percentage === '' ? null : (parseFloat(formData.markup_percentage) || 0),
    };
    onSubmit(submitData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const createCategoryInline = async () => {
    if (!newCatName.trim()) { alert('Enter a category name'); return; }
    setSavingAux(true);
    try {
      const r = await fetch(`${backendUrl}/api/consumables/categories`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCatName.trim(), markup_percentage: parseFloat(newCatMarkup) || 0 }),
      });
      if (r.ok) {
        const cat = await r.json();
        if (onListsChanged) await onListsChanged();
        setIsCustomCategory(false);
        setFormData(prev => ({ ...prev, category: cat.name, markup_percentage: markupTouched ? prev.markup_percentage : Number(cat.markup_percentage || 0) }));
        setNewCatName(''); setNewCatMarkup('');
      } else { const e = await r.json(); alert(e.detail || 'Failed to create category'); }
    } catch { alert('Error creating category'); }
    setSavingAux(false);
  };

  const createSupplierInline = async () => {
    if (!newSup.name.trim()) { alert('Enter a supplier name'); return; }
    setSavingAux(true);
    try {
      const r = await fetch(`${backendUrl}/api/consumables/suppliers`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newSup),
      });
      if (r.ok) {
        const sup = await r.json();
        if (onListsChanged) await onListsChanged();
        setIsCustomSupplier(false);
        setFormData(prev => ({ ...prev, supplier: sup.name }));
        setNewSup({ name: '', contact_number: '', account_manager: '', email: '' });
      } else { const e = await r.json(); alert(e.detail || 'Failed to create supplier'); }
    } catch { alert('Error creating supplier'); }
    setSavingAux(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="bg-white border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-900">{title}</CardTitle>
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-900 transition-colors"
            >
              ✕
            </button>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Item Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Item Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  data-testid="consumable-name-input"
                  className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter item name"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Category *
                </label>
                <div className="flex gap-2">
                  <select
                    value={isCustomCategory ? 'custom' : formData.category}
                    data-testid="consumable-category-select"
                    onChange={(e) => {
                      if (e.target.value === 'custom') {
                        setIsCustomCategory(true);
                      } else {
                        setIsCustomCategory(false);
                        selectCategory(e.target.value);
                      }
                    }}
                    className="flex-1 px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat.id || cat.name} value={cat.name}>{cat.name}{cat.markup_percentage ? ` (${cat.markup_percentage}%)` : ''}</option>
                    ))}
                    {formData.category && !categories.some(c => c.name === formData.category) && (
                      <option value={formData.category}>{formData.category} (new)</option>
                    )}
                    {canManageCategories && <option value="custom">+ Add New Category</option>}
                  </select>
                </div>
                {isCustomCategory && canManageCategories && (
                  <div className="mt-2 p-3 bg-slate-100 rounded-lg space-y-2">
                    <input
                      type="text"
                      value={newCatName}
                      onChange={(e) => setNewCatName(e.target.value)}
                      data-testid="new-category-name-input"
                      className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="New category name"
                    />
                    <input
                      type="number"
                      step="0.1"
                      value={newCatMarkup}
                      onChange={(e) => setNewCatMarkup(e.target.value)}
                      data-testid="new-category-markup-input"
                      className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Default markup % for this category"
                    />
                    <div className="flex gap-2">
                      <button type="button" onClick={createCategoryInline} disabled={savingAux} data-testid="new-category-create-btn"
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm disabled:opacity-50">Create</button>
                      <button type="button" onClick={() => { setIsCustomCategory(false); }} className="px-3 py-1.5 bg-slate-200 text-slate-900 rounded text-sm">Cancel</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Supplier */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Supplier
                </label>
                <div className="flex gap-2">
                  <select
                    value={isCustomSupplier ? 'custom' : formData.supplier}
                    data-testid="consumable-supplier-select"
                    onChange={(e) => {
                      if (e.target.value === 'custom') {
                        setIsCustomSupplier(true);
                      } else {
                        setIsCustomSupplier(false);
                        handleChange('supplier', e.target.value);
                      }
                    }}
                    className="flex-1 px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select supplier</option>
                    {suppliers.map(sup => (
                      <option key={sup.id || sup.name} value={sup.name}>{sup.name}</option>
                    ))}
                    {canEditItems && <option value="custom">+ Add New Supplier</option>}
                  </select>
                </div>
                {isCustomSupplier && canEditItems && (
                  <div className="mt-2 p-3 bg-slate-100 rounded-lg space-y-2">
                    <input type="text" value={newSup.name} onChange={(e) => setNewSup({ ...newSup, name: e.target.value })}
                      data-testid="new-supplier-name-input"
                      className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Supplier name *" />
                    <input type="text" value={newSup.contact_number} onChange={(e) => setNewSup({ ...newSup, contact_number: e.target.value })}
                      data-testid="new-supplier-contact-input"
                      className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Contact number" />
                    <input type="text" value={newSup.account_manager} onChange={(e) => setNewSup({ ...newSup, account_manager: e.target.value })}
                      data-testid="new-supplier-manager-input"
                      className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Account manager" />
                    <input type="email" value={newSup.email} onChange={(e) => setNewSup({ ...newSup, email: e.target.value })}
                      data-testid="new-supplier-email-input"
                      className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Email" />
                    <div className="flex gap-2">
                      <button type="button" onClick={createSupplierInline} disabled={savingAux} data-testid="new-supplier-create-btn"
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm disabled:opacity-50">Create</button>
                      <button type="button" onClick={() => { setIsCustomSupplier(false); }} className="px-3 py-1.5 bg-slate-200 text-slate-900 rounded text-sm">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Pricing Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Unit Price * ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.unit_price}
                  onChange={(e) => handleChange('unit_price', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Box/Bulk Price ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.box_price}
                  onChange={(e) => handleChange('box_price', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Box Quantity
                </label>
                <input
                  type="number"
                  value={formData.box_quantity}
                  onChange={(e) => handleChange('box_quantity', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="1"
                />
              </div>
            </div>

            {/* Markup & Sell Price */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Markup % {formData.category ? `(category default: ${categoryMarkup(formData.category)}%)` : ''}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.markup_percentage}
                  data-testid="consumable-markup-input"
                  disabled={!canEditItems}
                  onChange={(e) => { setMarkupTouched(true); handleChange('markup_percentage', e.target.value); }}
                  className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                  placeholder="Inherits category markup if blank"
                />
                <p className="text-xs text-slate-500 mt-1">Leave blank to use the category markup. Changing it pins this item.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Sell Price (preview)</label>
                <div data-testid="consumable-sell-preview" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-emerald-700 font-semibold">
                  ${(() => {
                    const base = parseFloat(formData.unit_price) || 0;
                    const mk = formData.markup_percentage === '' ? categoryMarkup(formData.category) : (parseFloat(formData.markup_percentage) || 0);
                    return (base * (1 + mk / 100)).toFixed(2);
                  })()}
                </div>
              </div>
            </div>

            {/* Specifications */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Size/Specification
                </label>
                <input
                  type="text"
                  value={formData.size}
                  onChange={(e) => handleChange('size', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 4.5&quot; x 1/8&quot; x 7/8&quot;"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Unit
                </label>
                <select
                  value={formData.unit}
                  onChange={(e) => handleChange('unit', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Each">Each</option>
                  <option value="Box">Box</option>
                  <option value="Pack">Pack</option>
                  <option value="Lb">Lb</option>
                  <option value="Kg">Kg</option>
                  <option value="Ft">Ft</option>
                  <option value="M">M</option>
                  <option value="Roll">Roll</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Additional notes or specifications..."
              />
            </div>

            {/* Form Actions */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
              >
                {item ? 'Update Item' : 'Add Item'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-slate-200 hover:bg-slate-100 text-slate-900 py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// ── Manage Categories Modal ──
const ManageCategoriesModal = ({ categories = [], token, backendUrl, onClose, onChanged }) => {
  const [rows, setRows] = useState(categories.map(c => ({ ...c })));
  const [newName, setNewName] = useState('');
  const [newMarkup, setNewMarkup] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setRows(categories.map(c => ({ ...c }))); }, [categories]);

  const saveRow = async (row) => {
    setBusy(true);
    try {
      const r = await fetch(`${backendUrl}/api/consumables/categories/${row.id}`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: row.name, markup_percentage: parseFloat(row.markup_percentage) || 0 }),
      });
      if (r.ok) { toast.success('Category updated — non-overridden items re-priced'); onChanged && onChanged(); }
      else { const e = await r.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Error'); }
    setBusy(false);
  };
  const delRow = async (row) => {
    if (!window.confirm(`Delete category "${row.name}"?`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${backendUrl}/api/consumables/categories/${row.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { toast.success('Deleted'); onChanged && onChanged(); }
      else { const e = await r.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Error'); }
    setBusy(false);
  };
  const addNew = async () => {
    if (!newName.trim()) { toast.error('Enter a name'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${backendUrl}/api/consumables/categories`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim(), markup_percentage: parseFloat(newMarkup) || 0 }),
      });
      if (r.ok) { toast.success('Category added'); setNewName(''); setNewMarkup(''); onChanged && onChanged(); }
      else { const e = await r.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Error'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card data-testid="manage-categories-modal" className="bg-white border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-900">Manage Categories & Markup</CardTitle>
            <button onClick={onClose} data-testid="mc-close-btn" className="text-slate-500 hover:text-slate-900"><X className="h-5 w-5" /></button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-[1fr_90px_auto] gap-2 items-end p-3 bg-slate-100 rounded-lg">
            <div>
              <label className="block text-xs text-slate-500 mb-1">New category</label>
              <input value={newName} onChange={e => setNewName(e.target.value)} data-testid="mc-new-name" className="w-full px-2 py-1.5 bg-slate-100 border border-slate-300 rounded text-slate-900 text-sm" placeholder="Name" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Markup %</label>
              <input type="number" step="0.1" value={newMarkup} onChange={e => setNewMarkup(e.target.value)} data-testid="mc-new-markup" className="w-full px-2 py-1.5 bg-slate-100 border border-slate-300 rounded text-slate-900 text-sm" placeholder="0" />
            </div>
            <button onClick={addNew} disabled={busy} data-testid="mc-add-btn" className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm disabled:opacity-50">Add</button>
          </div>
          <div className="space-y-2">
            {rows.map((row, i) => (
              <div key={row.id} className="grid grid-cols-[1fr_90px_auto_auto] gap-2 items-center">
                <input value={row.name} onChange={e => { const v = [...rows]; v[i] = { ...row, name: e.target.value }; setRows(v); }}
                  className="px-2 py-1.5 bg-slate-100 border border-slate-300 rounded text-slate-900 text-sm" />
                <input type="number" step="0.1" value={row.markup_percentage ?? 0} data-testid={`mc-markup-${row.id}`}
                  onChange={e => { const v = [...rows]; v[i] = { ...row, markup_percentage: e.target.value }; setRows(v); }}
                  className="px-2 py-1.5 bg-slate-100 border border-slate-300 rounded text-slate-900 text-sm" />
                <button onClick={() => saveRow(rows[i])} disabled={busy} data-testid={`mc-save-${row.id}`} className="px-2 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs">Save</button>
                <button onClick={() => delRow(rows[i])} disabled={busy} className="p-1.5 text-slate-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {rows.length === 0 && <p className="text-center text-slate-500 py-4 text-sm">No categories yet.</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ── Manage Suppliers Modal ──
const ManageSuppliersModal = ({ suppliers = [], token, backendUrl, onClose, onChanged }) => {
  const [rows, setRows] = useState(suppliers.map(s => ({ ...s })));
  const [newSup, setNewSup] = useState({ name: '', contact_number: '', account_manager: '', email: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setRows(suppliers.map(s => ({ ...s }))); }, [suppliers]);

  const saveRow = async (row) => {
    setBusy(true);
    try {
      const r = await fetch(`${backendUrl}/api/consumables/suppliers/${row.id}`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: row.name, contact_number: row.contact_number || '', account_manager: row.account_manager || '', email: row.email || '' }),
      });
      if (r.ok) { toast.success('Supplier updated'); onChanged && onChanged(); }
      else { const e = await r.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Error'); }
    setBusy(false);
  };
  const delRow = async (row) => {
    if (!window.confirm(`Delete supplier "${row.name}"?`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${backendUrl}/api/consumables/suppliers/${row.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { toast.success('Deleted'); onChanged && onChanged(); }
      else { const e = await r.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Error'); }
    setBusy(false);
  };
  const addNew = async () => {
    if (!newSup.name.trim()) { toast.error('Enter a supplier name'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${backendUrl}/api/consumables/suppliers`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newSup),
      });
      if (r.ok) { toast.success('Supplier added'); setNewSup({ name: '', contact_number: '', account_manager: '', email: '' }); onChanged && onChanged(); }
      else { const e = await r.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Error'); }
    setBusy(false);
  };

  const field = (row, i, key, ph) => (
    <input value={row[key] || ''} onChange={e => { const v = [...rows]; v[i] = { ...row, [key]: e.target.value }; setRows(v); }}
      className="px-2 py-1.5 bg-slate-100 border border-slate-300 rounded text-slate-900 text-sm" placeholder={ph} />
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card data-testid="manage-suppliers-modal" className="bg-white border-slate-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-900">Manage Suppliers</CardTitle>
            <button onClick={onClose} data-testid="ms-close-btn" className="text-slate-500 hover:text-slate-900"><X className="h-5 w-5" /></button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="p-3 bg-slate-100 rounded-lg space-y-2">
            <p className="text-xs text-slate-500">Add a new supplier</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input value={newSup.name} onChange={e => setNewSup({ ...newSup, name: e.target.value })} data-testid="ms-new-name" className="px-2 py-1.5 bg-slate-100 border border-slate-300 rounded text-slate-900 text-sm" placeholder="Supplier name *" />
              <input value={newSup.contact_number} onChange={e => setNewSup({ ...newSup, contact_number: e.target.value })} data-testid="ms-new-contact" className="px-2 py-1.5 bg-slate-100 border border-slate-300 rounded text-slate-900 text-sm" placeholder="Contact number" />
              <input value={newSup.account_manager} onChange={e => setNewSup({ ...newSup, account_manager: e.target.value })} data-testid="ms-new-manager" className="px-2 py-1.5 bg-slate-100 border border-slate-300 rounded text-slate-900 text-sm" placeholder="Account manager" />
              <input value={newSup.email} onChange={e => setNewSup({ ...newSup, email: e.target.value })} data-testid="ms-new-email" className="px-2 py-1.5 bg-slate-100 border border-slate-300 rounded text-slate-900 text-sm" placeholder="Email" />
            </div>
            <button onClick={addNew} disabled={busy} data-testid="ms-add-btn" className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm disabled:opacity-50">Add Supplier</button>
          </div>
          <div className="space-y-2">
            {rows.map((row, i) => (
              <div key={row.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_auto_auto] gap-2 items-center border-b border-slate-200 pb-2">
                {field(row, i, 'name', 'Name')}
                {field(row, i, 'contact_number', 'Contact')}
                {field(row, i, 'account_manager', 'Account mgr')}
                {field(row, i, 'email', 'Email')}
                <button onClick={() => saveRow(rows[i])} disabled={busy} data-testid={`ms-save-${row.id}`} className="px-2 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs">Save</button>
                <button onClick={() => delRow(rows[i])} disabled={busy} className="p-1.5 text-slate-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {rows.length === 0 && <p className="text-center text-slate-500 py-4 text-sm">No suppliers yet.</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Consumables;